import { BaseResource } from './base';
import {
  Activity,
  CreateActivityDto,
  UpdateActivityDto,
  SearchActivitiesParams,
  CreateActivityNotificationDto,
  UpdateActivityNotificationDto,
  ActivityNotificationDto,
  ActivityStats,
  ActivityStatsParams,
  EligibleClientsResponse,
  DeleteActivityParams,
} from '../models/activities';
import { RequestOptions, PaginatedResponseDto } from '../types';

export class ActivitiesResource extends BaseResource {
  private basePath = 'activities';

  async createActivity(data: CreateActivityDto, options?: RequestOptions): Promise<Activity> {
    return this.client.post<Activity>(this.basePath, data, options);
  }

  async searchActivities(
    params?: SearchActivitiesParams,
    options?: RequestOptions,
  ): Promise<PaginatedResponseDto<Activity>> {
    return this.paginate<Activity>(this.basePath, params as any, options);
  }

  async getActivity(id: string, options?: RequestOptions): Promise<Activity> {
    return this.client.get<Activity>(`${this.basePath}/${id}`, undefined, options);
  }

  async updateActivity(
    id: string,
    data: UpdateActivityDto,
    options?: RequestOptions,
  ): Promise<Activity> {
    return this.client.patch<Activity>(`${this.basePath}/${id}`, data, options);
  }

  async deleteActivity(
    id: string,
    params?: DeleteActivityParams,
    options?: RequestOptions,
  ): Promise<void> {
    const queryString = params?.deleteRecurrence ? '?deleteRecurrence=true' : '';
    return this.client.delete<void>(`${this.basePath}/${id}${queryString}`, options);
  }

  async createNotification(
    activityId: string,
    data: CreateActivityNotificationDto,
    options?: RequestOptions,
  ): Promise<ActivityNotificationDto> {
    return this.client.post<ActivityNotificationDto>(
      `${this.basePath}/${activityId}/notifications`,
      data,
      options,
    );
  }

  async getNotifications(
    activityId: string,
    options?: RequestOptions,
  ): Promise<{ data: ActivityNotificationDto[] }> {
    return this.client.get<{ data: ActivityNotificationDto[] }>(
      `${this.basePath}/${activityId}/notifications`,
      undefined,
      options,
    );
  }

  async updateNotification(
    activityId: string,
    notificationId: string,
    data: UpdateActivityNotificationDto,
    options?: RequestOptions,
  ): Promise<ActivityNotificationDto> {
    return this.client.patch<ActivityNotificationDto>(
      `${this.basePath}/${activityId}/notifications/${notificationId}`,
      data,
      options,
    );
  }

  async deleteNotification(
    activityId: string,
    notificationId: string,
    options?: RequestOptions,
  ): Promise<void> {
    return this.client.delete<void>(
      `${this.basePath}/${activityId}/notifications/${notificationId}`,
      options,
    );
  }

  async getStats(params?: ActivityStatsParams, options?: RequestOptions): Promise<ActivityStats> {
    return this.client.get<ActivityStats>(`${this.basePath}/stats`, params, options);
  }

  async getEligibleClients(
    activityId: string,
    options?: RequestOptions,
  ): Promise<EligibleClientsResponse> {
    return this.client.get<EligibleClientsResponse>(
      `${this.basePath}/${activityId}/eligible-clients`,
      undefined,
      options,
    );
  }
}
